import './landing.css';

import Swiper from 'swiper';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/swiper-bundle.css';
import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../../../core/event-bus';
import { Util } from '../../../utils/util';
import { ILandingBannerData, ILandingModules } from './landing.interface';
import { CookiePreferences, OrderType, Pages, StoreListType, DataLayerPromotionLocation, AddressValidation } from '../../../types/enums';
import { OrderTypeSelector } from '../../shared/OrderTypeSelector/order-type-selector';
import { IAddressFormat } from '../../../interfaces/global.interfaces';
import { ProfileService } from '../../../services/profile-service';
import { Page } from '../page';
import { Data } from '../../../models/data';
import { OrderService } from '../../../services/order-service';
import { DataLayerService } from '../../../services/data-layer-service';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { IDataDesktopBanner, IStoreLocation } from '../../../models/data.interface';
import { LocationService } from '../../../services/location-service';
import { IUserDefaultStore } from '../../../models/user.interface';
import { DialogCreators } from '../../../utils/dialog-creators';
import { OrderLocationBar } from '../../shared/OrderLocationBar/order-location-bar';
import { BannerService } from '../../../services/banner-service';
import { IRegionalizedBanner } from '../../../services/banner-service.interface';
import { ITempAddress } from '../../../models/context.interface';

/**
 * Represents the landing page of the application.
 */
export class Landing extends Page {
    private readonly _orderService: OrderService;
    private readonly _profileService: ProfileService;
    private readonly _locationService: LocationService;
    private readonly _bannerService: BannerService;
    private readonly _dataLayer: DataLayerService;
    //private _bannerData: ILandingBannerData;
    private _selectedStoreKey: string = "";
    private _closestDeliveryStoreKey: string = "";
    private _deliveryAddress: ITempAddress | null = null;
    private readonly _observers: IntersectionObserver[] = [];
    private readonly _nearestStores: { hasRun: boolean, stores: IStoreLocation[] } = { hasRun: false, stores: [] };
    private _userLocation: { latitude: number, longitude: number } | null = null;
    private _userLocationPromise: Promise<{ latitude: number; longitude: number } | null> | null = null;
    private _nearestStoresLoadedFromLocation = false;
    private _waitingForPermission: boolean = false;
    private _swiperInstances: { [key: string]: Swiper } = {};
    private readonly _modules: ILandingModules;
    private _regionalizedBanners: IRegionalizedBanner[] = [];
    private _resizeObserver: ResizeObserver | null = null;
    private _bannersInitialized: boolean = false;

    /**
     * Creates an instance of the Landing page.
     * @param eventBus - The event bus for handling events.
     * @param dataService - The service managing application data.
     * @param orderService - The service handling order-related operations.
     * @param profileService - The service managing user profile operations.
     * 
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, orderService: OrderService,
        profileService: ProfileService, locationService: LocationService, dataLayer: DataLayerService, bannerService: BannerService) {
        super("page_landing", eventBus, data, context, localizationService);
        this._orderService = orderService;
        this._profileService = profileService;
        this._locationService = locationService;
        this._bannerService = bannerService;
        this._dataLayer = dataLayer;

        this._modules = this._initializeModules();

        this._setupEventBusListeners();
        this._setupEventListeners();

        this._setupResizeObserver();
    }


    private async _displayBannersAsync(force: boolean = false): Promise<void> {
        if (this._bannersInitialized && !force) return;

        this._bannersInitialized = true;

        const brandStoreKey = this._context.getProperty("brandStoreKey");
        if (!brandStoreKey) {
            this._hideBannerContainers();
            return;
        }

        const userLocation = this._userLocation;

        this._regionalizedBanners = await this._bannerService.getApplicableBannersAsync(
            brandStoreKey,
            userLocation?.latitude ?? null,
            userLocation?.longitude ?? null
        );

        if (this._regionalizedBanners.length === 0) {
            this._hideBannerContainers();
            return;
        }

        this._renderBanners();

        this._setupPromotionObservers();
    }

    private _hideBannerContainers(): void {
        Util.hideElement("div_landing_banner_main_parent");
        Util.hideElement("div_landing_banner_placeholder");
    }



    private _renderBanners(): void {
        const container = document.getElementById("div_landing_banner_main");
        if (!container) return;

        container.innerHTML = "";

        this._regionalizedBanners.forEach((banner, index) => {
            const imageUrl = this._bannerService.getBannerImageUrlForScreenSize(banner);
            if (!imageUrl) return;

            const slideElement = this._createBannerSlide(banner, imageUrl, index);
            container.appendChild(slideElement);
        });

        this._initializeBannerCarousel("main", this._regionalizedBanners.length);

        Util.showElement("div_landing_banner_main_parent");
        Util.hideElement("div_landing_banner_placeholder");

        this._updateSwiperInstance();
    }

    private _createBannerSlide(
        banner: IRegionalizedBanner, 
        imageUrl: string, 
        index: number
    ): HTMLElement {
        const html = `
            <div id="div_landing_banner_${banner.onlineBannerID}" class="swiper-slide">
                <img src="${imageUrl}" 
                     alt="${banner.name}" 
                     loading="lazy"
                     data-banner-id="${banner.onlineBannerID}"
                     data-coupon-id="${banner.couponID || ''}" />
            </div>`;

        const element = Util.createHtmlElementFromTemplate(html);
        const imageElement = element.querySelector('img') as HTMLImageElement;

        if (banner.couponID) {
            imageElement.onclick = () => this._handleBannerClick(banner, index);
        }

        return element;
    }

    private _handleBannerClick(banner: IRegionalizedBanner, index: number): void {
        if (!banner.couponID) return;

        this._context.setProperty("couponId", banner.couponID);
        document.getElementById("btn_landing_order_now")?.click();

        if (this._dataLayer) {
            this._dataLayer.select_promotion(
                banner.couponID,
                banner.couponNames ?? [], 
                DataLayerPromotionLocation.LANDING_BANNER,
                index
            );
        }
    }

    private _setupResizeObserver(): void {
        this._resizeObserver = new ResizeObserver(() => {
            if (this._regionalizedBanners.length > 0) {
                this._updateBannerImages();
            }
        });

        this._resizeObserver.observe(document.body);
    }

    private _updateBannerImages(): void {
        const imageLoadPromises: Promise<void>[] = [];

        this._regionalizedBanners.forEach(banner => {
            const imageUrl = this._bannerService.getBannerImageUrlForScreenSize(banner);
            if (!imageUrl) return;

            const imgElement = document.querySelector(
                `img[data-banner-id="${banner.onlineBannerID}"]`
            ) as HTMLImageElement;

            if (imgElement && imgElement.src !== imageUrl) {
                const loadPromise = new Promise<void>((resolve) => {
                    if (imgElement.complete) {
                        resolve();
                    } else {
                        imgElement.onload = () => resolve();
                        imgElement.onerror = () => resolve(); // Still resolve on error
                    }
                });
                imageLoadPromises.push(loadPromise);
                imgElement.src = imageUrl;
            }
        });

        // Update Swiper after all images have loaded
        if (imageLoadPromises.length > 0) {
            Promise.all(imageLoadPromises).then(() => this._updateSwiperInstance());
        }
    }

    private _updateSwiperInstance() {
        const swiperInstance = this._swiperInstances['main'];
        if (swiperInstance) {
            swiperInstance.update();
        }
    }

     private _setupPromotionObservers(): void {
        if (!this._dataLayer) return;

        this._observers.forEach(x => x.disconnect());
        this._observers.length = 0;

        this._regionalizedBanners.forEach((banner, index) => {
            if (!banner.couponID) return;

            const element = document.getElementById(`div_landing_banner_${banner.onlineBannerID}`);
            if (element) {
                const couponName = banner.couponNames 
                    ? this._localizationService.GetName(banner.couponNames)
                    : banner.name;

                this._observers.push(this._setupPromotionObserver(element, index, banner.couponID, couponName));
            }
        });
    }

    public dispose(): void {
        this._resizeObserver?.disconnect();
        this._observers.forEach(x => x.disconnect());
    }

    /**
     * Initializes the banner data model.
     * @returns The initialized banner data object.
     */
    private _initializeBannerModel(): ILandingBannerData {
        return {
            desktop: null,
            mobile: null
        };
    }

    private _initializeModules(): ILandingModules {
        return {
            orderLocationBar: new OrderLocationBar(this._eventBus, this._data, this._context, this._localizationService, this._orderService, this._profileService)
        };
    }

    /**
     * Initializes the landing page asynchronously.
     */
    public async initAsync(): Promise<void> {
        await this._renderPageAsync();
    }

    public override show(): void {
        super.show();

        if (this._data.isBrandLoaded)
            this._renderPageAsync();

        this._context.setProperty("upsellSelected", false);
        if (this._dataLayer)
            this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.storeKey ? this._data.getProperty("StoreInfo").storeCode.toString() : '0', this._profileService.user.user);
        }

    /**
     * Sets up event listeners for handling various application events.
     */
    private _setupEventBusListeners() {
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED, this._handleOrderTypeChange);
        this._eventBus.on(EventTypes.LOGGED_IN, this._handleLoggedInEvent);
        this._eventBus.on(EventTypes.LOGGED_OUT, this._handleLoggedOutEvent);
        this._eventBus.on(EventTypes.BRAND_DATA_RETRIEVED, () => this.initAsync());
        this._eventBus.on(EventTypes.COOKIE_PREFERENCE, this._syncStoreCacheToPreference);
        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, this._languageChanged);
        this._eventBus.on(EventTypes.DATALAYER_LOADED, () => {
            //initial attempt to capture page_view may not have had the dataLayer loaded yet
            if (this._element?.classList.contains("open"))
                this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.storeKey ? this._data.getProperty("StoreInfo").storeCode.toString() : '0', this._profileService.user.user);

            this._setupPromotionObservers();
        });
    }

    /**
     * Renders the landing page asynchronously and sets up promotion observers after rendering elements.
     */
    private async _renderPageAsync(): Promise<void> {
        this._setLocationBar();
        this._updateLandingStoreLocation();

        this._buildOrderTypeSelectionBar();
        this._fetchNearestStoresAsync(); // Leave Asynchronous, can load in the background
        await this._displayBannersAsync(); // Load banners and show generic banners w/ no location
    }

    private _setLocationBar(): void {
        const locationBar = document.getElementById("div_landing_location");
        if (locationBar) {
            locationBar.innerHTML = "";
            locationBar.append(this._modules.orderLocationBar.element);
        }
    }

    /**
     * Gets the currently selected order type ID or defaults to TAKE_OUT.
     * @returns The selected order type ID.
     */
    private _getSelectedOrderTypeId(): OrderType {
        return this._orderService.order.getProperty("OrderTypeID") ?? this._context.getProperty("defaultOrderType");
    }

    /**
     * Sets up UI event listeners for user interactions.
     */
    private _setupEventListeners(): void {
        Util.setElement("onclick", "btn_landing_order_now", this._handleLandingStartOrderClickAsync);
        Util.setElement("onclick", "btn_landing_order_now_mobile", this._handleLandingStartOrderClickAsync);
    }

    private readonly _handleLoggedInEvent = async (): Promise<void> => {
        const brandId = this._data.getBrandProperty("StoreInfo").brandId;
        this._closestDeliveryStoreKey = await this._locationService.getClosestDeliveryStoreKeyByUserAsync(brandId, this._profileService.user);
        this._updateLandingStoreLocation();
    }

    private readonly _handleLoggedOutEvent = (): void => {
        this._closestDeliveryStoreKey = "";
        this._updateLandingStoreLocation();
    }

    /**
     * Handles the event when the user selects a location.
     */
    private readonly _handleLandingLocationClick = (): void => {
        const orderTypeId = this._getSelectedOrderTypeId();
        let addressId: number | null = null;

        if (orderTypeId == OrderType.DELIVERY) {
            const primaryAddress = this._profileService.user.getProperty("Addresses")?.find(x => x.IPRM);
            addressId = primaryAddress?.AID || null;
        }

        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, {
            orderTypeId: orderTypeId,
            selectedStoreKey: this._selectedStoreKey,
            addressId: addressId,
            forChangingStore: true,
            nearbyOnly: false,
            storeListType: StoreListType.NEARBY
        });
    }

    /**
    * Handles the event when the user clicks the start order button(s).
    */
    private readonly _handleLandingStartOrderClickAsync = async (): Promise<void> => {
        if (this._selectedStoreKey) {
            const orderTypeId = this._orderService.order.getProperty("OrderTypeID");

            if (orderTypeId === OrderType.DELIVERY) {
                await this._handleStartOrderForDelivery(this._selectedStoreKey);
            } else {
                await this._handleStartOrderForNonDelivery(this._selectedStoreKey);
            }
        } else {
            // No store selected -- redirect externally if Setting 274 configures a store locator URL (website only).
            const storeLocatorRedirectUrl = this._data.getBrandProperty("Settings")?.OOS?.storeLocatorRedirectUrl;
            if (!Util.isAppView() && storeLocatorRedirectUrl) {
                Util.redirect(storeLocatorRedirectUrl);
            } else {
                this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, { orderTypeId: this._getSelectedOrderTypeId() });
            }
        }

        if (this._dataLayer) {
            const btnText = document.getElementById("btn_landing_order_now")?.textContent ?? '';
            this._dataLayer.start_order_click(btnText, 'Homepage');
        }
    }

    private readonly _handleStartOrderForNonDelivery = async (storeKey: string): Promise<void> => {
        if (!storeKey) return;

        if (this._isStoreTooFar(storeKey)) {
            this._showStoreDistanceWarning(storeKey);
        }
        else {
            this._triggerStoreChange(storeKey);
        }
    }

    private readonly _triggerStoreChange = (storeKey: string): void => {
        this._eventBus.emit(EventTypes.STORE_CHANGED, storeKey, this._profileService.loyaltyProvider);
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.MENU);
    }


    private readonly _isStoreTooFar = (storeKey: string): boolean => {
        const storeDistance = this._data.locations.find(x => x.storeKey === storeKey)?.distance ?? 0;
        if (storeDistance > 5) {
            return true;
        }
        return false;
    }

    private _showStoreDistanceWarning(storeKey: string): void {
        const orderType = this._orderService.order.getProperty("OrderTypeID");
        const storeAddress = this._getStoreFormattedAddress(storeKey);

        let distanceText: string = this._getDistanceTextForStore(storeKey);
        distanceText = `<span class="highlight">${distanceText}</span>`;

        const dineInTextNames = this._data.getBrandProperty("OrderTypeSubTypes").find(x => x.OrderTypeId === OrderType.DINE_IN)?.Names;
        const dineInText = this._localizationService.GetName(dineInTextNames ?? []);

        const takeOutTextNames = this._data.getBrandProperty("OrderTypeSubTypes").find(x => x.OrderTypeId === OrderType.TAKE_OUT)?.Names;
        const takeOutText = this._localizationService.GetName(takeOutTextNames ?? []);

        const orderTypeText: string = orderType === OrderType.DINE_IN
            ? dineInText
            : takeOutText;

        const dialogMessage: string = this._localizationService
            .names("StoreDistanceDialogMessage")
            .replace("{{order_type}}", orderTypeText)
            .replace("{{store_address}}", storeAddress)
            .replace("{{store_distance}}", distanceText);

        DialogCreators.messageBox(dialogMessage, [
            { text: this._localizationService.names("No"), callBack: null },
            { text: this._localizationService.names("Yes"), callBack: this._triggerStoreChange.bind(this, storeKey) },
        ]);
    }

    private _getStoreFormattedAddress(storeKey: string): string {
        const store = this._data.locations.find(x => x.storeKey === storeKey);

        if (!store) return "";

        const formattedAddress = Util.formatAddressObject({
            StreetNo: store.streetNumber,
            Address1: store.address1,
            Address2: store.address2,
            Address3: store.address3,
            Address4: store.address4,
            Address5: store.address5,
            City: store.city,
            State: store.state,
            ZipCode: store.zip,
            CountryID: store.countryId || 1,
            AddressTypeID: 0
        }, this._data.getBrandProperty("Countries"));

        return `${formattedAddress.address1} ${formattedAddress.cityState}`;
    }

    private _getDistanceTextForStore(storeKey: string): string {
        const storeDistance = this._data.locations.find(x => x.storeKey === storeKey)?.distance ?? 0;

        const countryId = this._data.getBrandProperty("StoreInfo").countryId
        const isMetric = this._data.getBrandProperty("Countries").find(c => c.CountryID == countryId)?.UnitOfMeasurement === "Metric";

        const convertedDistance = isMetric ? Util.Float2(Util.ConvertMilesToKilometers(storeDistance)) : storeDistance;
        const unitsText = Util.GetDistanceUnits(convertedDistance, isMetric);

        const text = this._localizationService.names("StoreDistanceAway")
            .replace("{distance}", `${convertedDistance}`)
            .replace("{type}", this._localizationService.names(unitsText));

        return text;
    }

    private readonly _handleStartOrderForDelivery = async (storeKey: string): Promise<void> => {
        if (!storeKey) return;

        if (this._orderService.order.getProperty("Address") !== null) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.MENU);
            return;
        }

        if (!this._deliveryAddress) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, { orderTypeId: OrderType.DELIVERY });
            return;
        }

        const store = this._data.locations.find(x => x.storeKey === storeKey);
        if (!store) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, { orderTypeId: OrderType.DELIVERY });
            return;
        }

        if (store.zones.length === 0) {
            const deliveryZones = await this._locationService.loadDeliveryZonesForLocations([store.storeId]);
            store.zones = deliveryZones[0]?.zones ?? [];
        }

        const orderTime = this._locationService.resolveOrderTime(
            this._orderService.order.getProperty("FutureDate"),
            this._orderService.order.getProperty("FutureMinute"),
            this._data.getProperty("TimeOffset")
        );

        const result = this._locationService.findDeliveryZoneForAddress(
            this._deliveryAddress.Latitude,
            this._deliveryAddress.Longitude,
            [store],
            orderTime
        );

        if (result.status !== AddressValidation.VALID) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, { orderTypeId: OrderType.DELIVERY });
            return;
        }

        this._deliveryAddress.ZoneID = result.zoneId;
        this._orderService.order.setAddress(this._deliveryAddress);
        this._eventBus.emit(EventTypes.STORE_CHANGED, storeKey, this._profileService.loyaltyProvider);
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.MENU);
    }

    /**
     * Handles changes in the selected order type.
     * @param orderTypeId - The new order type ID.
     */
    private readonly _handleOrderTypeChange = (orderTypeId: OrderType): void => {
        this._updateLandingStoreLocation();
        this._updateOrderButtonText(orderTypeId);
    }

    /**
     * Updates the order button text based on the selected order type.
     * @param orderTypeId - The new order type ID.
     */
    private _updateOrderButtonText(orderTypeId: OrderType): void {
        const orderType = this._data.getBrandProperty("OrderTypeSubTypes").find(x => x.OrderTypeId === orderTypeId);
        const orderTypeName = orderType ? this._localizationService.GetName(orderType.Names) : "";

        Util.setElement("innerText", "btn_landing_order_now", this._localizationService.names("StartOrderWithOrderType").replace("{{order_type}}", orderTypeName));
        Util.setElement("innerText", "btn_landing_order_now_mobile", this._localizationService.names("StartOrderWithOrderType").replace("{{order_type}}", orderTypeName));
    }

    /**
     * Fetches and displays the nearest stores for the current brand.
     *
     * This method performs the full location→store-resolution flow:
     *
     * 1. Prevents duplicate executions by checking `hasRun`.
     * 2. Attempts to get the user's location with a timeout for initial UX.
     * 3. If location is available quickly -> loads nearest stores immediately.
     * 4. If location is not available before timeout:
     *      - Attempts to load from cached nearest stores.
     *      - If the user has not responded to the permission prompt yet,
     *        a background retry loop is scheduled to detect late approval.
     * 5. Ensures the location placeholder is always hidden via `finally`.
     *
     * This function guarantees that the placeholder is removed regardless
     * of whether location succeeds, fails, hangs, or is approved later.
     *
     * @returns {Promise<void>} A promise that resolves when store resolution
     * has completed or been deferred.
     */
    private async _fetchNearestStoresAsync(): Promise<void> {
        if (this._nearestStores.hasRun || !this._data.isBrandLoaded)
            return;

        this._nearestStores.hasRun = true;

        const brandId = this._data.getBrandProperty("StoreInfo").brandId;

        // Start location lookup once
        if (!this._userLocationPromise)
            this._userLocationPromise = this._getUserLocationAsync();

        const timeoutMs = 8000; 

        try {
            // Race: either we get location, or we give up for the *initial* UX
            const locationOrNull = await Promise.race([
                this._userLocationPromise,
                new Promise<null>(resolve => setTimeout(() => resolve(null), timeoutMs))
            ]);

            if (locationOrNull) {
                this._userLocation = locationOrNull;
                this._nearestStoresLoadedFromLocation = true;
                this._data.updateLocationsDistance(locationOrNull.latitude, locationOrNull.longitude);
                await this._loadStoresForLocationAsync(brandId, locationOrNull);
                if (this._userLocation) {
                    this._displayBannersAsync(true);
                }
            } else {
                // We timed out (or location was null): use cached store if we can
                const cached = this._getCachedNearestStore(brandId, 0, 0); // lat/lon ignored in that case
                if (cached) {
                    this._nearestStores.stores = cached;
                    this._updateLandingStoreLocation();
                }

                // If location failed due to waiting for permission, retry later
                if (this._waitingForPermission) {
                    this._scheduleLateLocationRetry(brandId);
                }
            }
        } catch (err) {
            console.error("Failed to fetch nearest stores:", err);
        } finally {
            // UI rule: placeholder ALWAYS goes away, no matter what
            this._hideLocationPlaceholder();
        }
    }

    /**
     * Schedules a retry loop that repeatedly attempts to acquire the user's
     * geolocation after the initial lookup timed out.
     *
     * This is required because some browsers (especially on mobile) return
     * an immediate geolocation error until the user explicitly interacts with
     * the permission prompt. Once the user eventually taps "Allow", a retry
     * will successfully return a position.
     *
     * The loop continues until:
     *   - A valid location is obtained → nearest stores are loaded.
     *   - The user explicitly denies permission → retries stop.
     *   - `maxAttempts` is reached → retries stop.
     *   - Stores have already been loaded from a previous location result.
     *
     * @param {string} brandId - The brand identifier used when fetching stores.
     * @returns {void}
     */
    private _scheduleLateLocationRetry(brandId: string): void {
        const maxAttempts = 20; // retry for ~10 seconds
        let attempts = 0;

        const tryAgain = async () => {
            if (this._nearestStoresLoadedFromLocation)
                return; // already done

            attempts++;

            const loc = await this._getUserLocationAsync();

            if (loc) {
                // SUCCESS – user finally allowed location
                this._userLocation = loc;
                this._nearestStoresLoadedFromLocation = true;

                try {
                    await this._loadStoresForLocationAsync(brandId, loc);
                    await this._displayBannersAsync(true); // refresh banners w/ location-specific data
                } catch (err) {
                    console.error("Late retry nearest stores failed:", err);
                }

                return; // stop retry loop
            }

            // User either denied or still hasn't allowed
            if (!this._waitingForPermission) {
                // User explicitly denied
                return;
            }

            if (attempts < maxAttempts) {
                setTimeout(tryAgain, 500);
            }
        };

        setTimeout(tryAgain, 500);
    }

    /**
     * Loads nearest stores for a given brand and geographic location.
     *
     * Logic flow:
     * 1. Checks if cached nearest stores exist within distance + expiration rules.
     *    If so, uses the cached result and updates the UI immediately.
     * 2. Otherwise, fetches nearest stores from the backend using the user's
     *    latitude and longitude.
     * 3. Saves the fetched result to cache.
     * 4. Updates the UI with the resolved store list.
     *
     * This method does not manage placeholders or user-permission flow; it only
     * handles the store-fetching and caching logic.
     *
     * @param {string} brandId - The brand identifier whose stores to query.
     * @param {{ latitude: number, longitude: number }} location - The user's coordinates.
     * @returns {Promise<void>} Resolves when the store data is loaded and applied.
     */
    private async _loadStoresForLocationAsync(
        brandId: string,
        location: { latitude: number; longitude: number }
    ): Promise<void> {
        const cached = this._getCachedNearestStore(brandId, location.latitude, location.longitude);
        if (cached) {
            this._nearestStores.stores = cached;
            this._updateLandingStoreLocation();
            return;
        }

        const orderTypeIds = this._getNonDeliveryOrderTypeIds();
        const stores = await this._locationService.getClosestStoresAsync(
            brandId,
            orderTypeIds,
            location.latitude,
            location.longitude
        );

        this._nearestStores.stores = stores;
        this._saveNearestStoreToCache(brandId, location.latitude, location.longitude, stores);
        this._updateLandingStoreLocation();
    }

    /**
     * Attempts to retrieve the user's geographic location.
     *
     * Behavior:
     * - If running in the native app view, attempts to read location from a stored cookie.
     * - If the browser does not support `navigator.geolocation`, returns `null`.
     * - Calls `_getPositionAsync()` to request the user's current coordinates.
     * - Sets internal flags indicating whether the request is pending user action,
     *   allowing the caller to schedule background retries if needed.
     * - Returns `null` when:
     *      - The user denies permission.
     *      - The browser blocks the request pending user interaction.
     *      - A timeout or error occurs.
     *
     * @returns {Promise<{ latitude: number, longitude: number } | null>}
     * A promise that resolves with the user's coordinates or `null` if unavailable.
     */
    private async _getUserLocationAsync(): Promise<{ latitude: number, longitude: number } | null> {
        if (Util.isAppView()) {
            const cookie = Util.getUserLocationFromCookie();
            return cookie ? { latitude: cookie.lat, longitude: cookie.lng } : null;
        }

        if (!("geolocation" in navigator))
            return null;

        try {
            return await this._getPositionAsync();
        } catch (err) {
            console.warn("Failed to get user position:", err);
            return null;
        }
    }

    /**
     * Wraps `navigator.geolocation.getCurrentPosition` in a Promise-based API.
     *
     * This method initiates a browser geolocation request and resolves with the
     * user's coordinates when successful. It also manages internal permission-state
     * flags so callers can determine if a retry loop should be scheduled.
     *
     * Behavior:
     * - Resolves with `{ latitude, longitude }` on success.
     * - Resolves with `null` when:
     *      - The user explicitly denies permission.
     *      - The browser delays geolocation until user interaction (common on mobile).
     *      - The request times out or encounters a non-fatal error.
     * - Does **not** reject except for exceptional cases; instead it returns `null`
     *   because callers treat "no location" as a normal control-flow state.
     *
     * @returns {Promise<{ latitude: number, longitude: number } | null>}
     * A promise resolving to the user's coordinates or `null` if unavailable.
     */
    private async _getPositionAsync(): Promise<{ latitude: number, longitude: number } | null> {
        if(Util.isAppView() && typeof getMobileAppLocation == "function"){
            this._waitingForPermission = true;
            const pos = await Util.getCurrentPositionAsync();
            this._waitingForPermission = false;
            return pos;
        }
        
        return new Promise(resolve => {
            navigator.geolocation.getCurrentPosition(
                pos => {
                    this._waitingForPermission = false;
                    resolve({
                        latitude: pos.coords.latitude,
                        longitude: pos.coords.longitude
                    });
                },
                err => {
                    // If user hasn't responded yet -> we'll retry later
                    if (err.code === err.PERMISSION_DENIED) {
                        this._waitingForPermission = false; // user explicitly denied
                        resolve(null);
                    } else {
                        // User hasn't interacted yet or browser requires gesture
                        this._waitingForPermission = true;
                        resolve(null);
                    }
                },
                {
                    enableHighAccuracy: false,
                    timeout: 8000,
                    maximumAge: 5 * 60 * 1000
                }
            );
        });
    }

    /**
     * Returns cached store data if valid and within 5 miles of user.
     */
    private _getCachedNearestStore(brandId: string, lat: number, lon: number): IStoreLocation[] | null {
        const storage = Util.getPreferredStorage();
        const key = `nearestStore:${brandId}`;
        const raw = storage.getItem(key);
        if (!raw)
            return null;

        try {
            const cached = JSON.parse(raw);
            const now = Date.now();

            const distMiles = Util.haversineMiles(cached.latitude, cached.longitude, lat, lon);
            if (now < cached.expires && distMiles <= 5)
                return cached.data;

        } catch {
            // ignore corrupted data
        }

        storage.removeItem(key);
        return null;
    }

    /**
     * Caches nearest store data with expiry (1 hour).
     */
    private _saveNearestStoreToCache(brandId: string, lat: number, lon: number, data: IStoreLocation[]): void {
        const storage = Util.getPreferredStorage();
        storage.setItem(`nearestStore:${brandId}`, JSON.stringify({
            latitude: lat,
            longitude: lon,
            data,
            expires: Date.now() + 1000 * 60 * 60 // Plus 1 hour
        }));
    }

    /**
     * Moves session storage to local storage based on user cookie preference.
     */
    private readonly _syncStoreCacheToPreference = (preference: CookiePreferences): void => {
        const brandId = this._data.getBrandProperty("StoreInfo").brandId;
        const key = `nearestStore:${brandId}`;

        if (preference === CookiePreferences.ACCEPT) {
            const raw = sessionStorage.getItem(key);
            if (raw) localStorage.setItem(key, raw);
        } else {
            const raw = localStorage.getItem(key);
            if (raw) sessionStorage.setItem(key, raw);
        }
    }

    /**
     * Extracts non-delivery order type IDs from brand config.
     */
    private _getNonDeliveryOrderTypeIds(): number[] {
        return [
            ...new Set(
                this._data
                    .getBrandProperty("OrderTypeSubTypes")
                    .filter(x => x.OrderTypeId !== OrderType.DELIVERY)
                    .map(x => x.OrderTypeId)
            )
        ];
    }

    /**
     * Updates the landing UI for the current order type with selected store info.
     */
    private readonly _updateLandingStoreLocation = (): void => {
        const orderTypeId = this._orderService.order.getProperty("OrderTypeID");

        switch (orderTypeId) {
            case OrderType.DINE_IN:
            case OrderType.TAKE_OUT:
                this._updateStoreLocationForNonDelivery();
                break;
            case OrderType.DELIVERY:
                this._updateStoreLocationForDelivery();
                break;
        }
    }

    /**
     * Hides the placeholder and shows the call-to-action location button.
     */
    private _hideLocationPlaceholder(): void {
        Util.hideElement("div_landing_location_placeholder");
        Util.showElement("div_landing_location");
    }

    /**
     * Updates the UI to display the store location for non-delivery orders (Dine-In or Takeout).
     */
    private _updateStoreLocationForNonDelivery(): void {
        const orderTypeId = this._orderService.order.getProperty("OrderTypeID");

        let defaultStore: IUserDefaultStore | null = null;

        if (this._data.storeKey) {
            const storeInfo = this._data.getProperty("StoreInfo");
            defaultStore = {
                StoreKey: this._data.storeKey,
                StreetNumber: storeInfo.streetNumber,
                Address1: storeInfo.address1,
                Address2: storeInfo.address2,
                CountryId: storeInfo.countryId
            };
        }

        if (this._profileService.user.isLoggedIn()) {
            defaultStore ??= this._profileService.user.getProperty("DefaultStore");
        }

        defaultStore ??= this._selectDefaultStore(orderTypeId);

        if (defaultStore) {
            this._selectedStoreKey = defaultStore.StoreKey;

            const tempAddress: IAddressFormat = {
                StreetNo: defaultStore.StreetNumber,
                Address1: defaultStore.Address1,
                Address2: defaultStore.Address2,
                Address3: "",
                Address4: "",
                Address5: "",
                City: "",
                State: "",
                ZipCode: "",
                CountryID: defaultStore.CountryId || 1,
                AddressTypeID: 0
            };

            this._modules.orderLocationBar.updateAddress(tempAddress);
            this._modules.orderLocationBar.storeKey = this._selectedStoreKey;

            Util.visibilityOnElement("div_landing_location");
        } else {
            Util.visibilityOffElement("div_landing_location");
            this._selectedStoreKey = "";
            this._modules.orderLocationBar.storeKey = this._selectedStoreKey;
        }
    }

    /**
     * Pick a default store that supports the given order type.
     * - If we don't have user location, returns the first matching nearest store.
     * - If we do, returns the nearest matching store by haversine distance.
     * @param {number} orderTypeId - The order type to filter by
     */
    private _selectDefaultStore(orderTypeId: number) {
        const stores = this._nearestStores.stores;
        if (stores.length === 0)
            return null;

        const matching = stores.filter(s => s.orderTypes?.includes(orderTypeId));
        if (matching.length === 0)
            return null;

        // If we don't know the user location, use the first matching "nearest" store
        if (!this._userLocation)
            return this._mapToDefaultStore(matching[0]);

        const { latitude: lat, longitude: lon } = this._userLocation;

        // Find the closest store by distance
        const closest = matching.reduce<{ store: IStoreLocation; dist: number; } | null>((best, s) => {
            const dist = Util.haversineMiles(lat, lon, s.latitude, s.longitude);
            if (!best || dist < best.dist)
                return { store: s, dist };

            return best;
        }, null)?.store;

        return closest ? this._mapToDefaultStore(closest) : null;
    }

    /**
     * Map a store location to the default store payload.
     * @param {IStoreLocation} store
     */
    private _mapToDefaultStore(store: IStoreLocation) {
        return {
            StoreKey: store.storeKey,
            StreetNumber: store.streetNumber,
            Address1: store.address1,
            Address2: store.address2,
            CountryId: store.countryId
        };
    }

    /**
     * Updates the UI to display the store location for delivery orders.
     */
    private _updateStoreLocationForDelivery(): void {
        const orderAddress = this._orderService.order.getProperty("Address");
        const lastOrderAddress = this._orderService.order.getLastDeliveryAddress();

        if (orderAddress) {
            this._deliveryAddress = orderAddress;
            const addressFormat: IAddressFormat = this._getAddressFormatFromTempAddress(orderAddress);
            this._modules.orderLocationBar.updateAddress(addressFormat);
            this._modules.orderLocationBar.storeKey = this._data.storeKey;
            this._selectedStoreKey = this._data.storeKey;
            Util.visibilityOnElement("div_landing_location");
            return;
        } else if (lastOrderAddress) {
            this._deliveryAddress = lastOrderAddress;

            const addressFormat: IAddressFormat = this._getAddressFormatFromTempAddress(lastOrderAddress);
            this._modules.orderLocationBar.updateAddress(addressFormat);

            const lastDeliveryStoreKey = this._orderService.order.getLastDeliveryAddressStore() ?? "";
            this._modules.orderLocationBar.storeKey = lastDeliveryStoreKey;
            this._selectedStoreKey = lastDeliveryStoreKey;

            Util.visibilityOnElement("div_landing_location");
            return;
        }

        const primaryAddress = this._profileService.user.getProperty("Addresses")?.find(x => x.IPRM);
        if (primaryAddress) {
            const tempAddress: IAddressFormat = {
                StreetNo: primaryAddress.STRNO,
                Address1: primaryAddress.ADDR1,
                Address2: primaryAddress.ADDR2,
                Address3: primaryAddress.ADDR3,
                Address4: primaryAddress.ADDR4,
                Address5: primaryAddress.ADDR5,
                City: primaryAddress.CITY,
                State: primaryAddress.STA,
                ZipCode: primaryAddress.ZIP,
                CountryID: primaryAddress.CID || 1,
                AddressTypeID: 0
            };

            this._selectedStoreKey = this._closestDeliveryStoreKey;
            this._deliveryAddress = Util.getTempAddressFromUserAddress(primaryAddress);

            this._modules.orderLocationBar.updateAddress(tempAddress);
            this._modules.orderLocationBar.storeKey = this._selectedStoreKey;

            Util.visibilityOnElement("div_landing_location");
        } else {
            Util.visibilityOffElement("div_landing_location");
            this._selectedStoreKey = "";
            this._modules.orderLocationBar.storeKey = this._selectedStoreKey;
        }
    }

    private _getAddressFormatFromTempAddress(tempAddress: ITempAddress): IAddressFormat {
        return {
            StreetNo: tempAddress.StreetNo,
            Address1: tempAddress.StreetName,
            Address2: tempAddress.Address2,
            Address3: tempAddress.Address3,
            Address4: tempAddress.Address4,
            Address5: tempAddress.Address5,
            City: tempAddress.City,
            State: tempAddress.State,
            ZipCode: tempAddress.Zip,
            CountryID: tempAddress.CountryID ?? 1,
            AddressTypeID: tempAddress.AddressTypeID
        };
    }

    /**
     * Creates banner image elements dynamically.
     */
    private _createBannerImageElements(parentId: string, bannerData: IDataDesktopBanner): void {
        const bannerContainer = document.getElementById(parentId);
        if (!bannerContainer)
            return

        bannerContainer.innerHTML = "";

        for (const [index, banner] of bannerData.Banners.entries()) {
            const html = `
                <div id="div_landing_banner_image_${bannerData.Id}_${index}" class="swiper-slide">
                    <img src="${banner.ImageURL}" alt="Banner ${index}" />
                </div>`;

            const element = Util.createHtmlElementFromTemplate(html);
            const imageElement = element.children[0] as HTMLImageElement;
            if (banner.CouponID !== 0) {
                imageElement.onclick = () => {
                    this._context.setProperty("couponId", banner.CouponID);
                    document.getElementById("btn_landing_order_now")?.click();
                    if (this._dataLayer)
                        this._dataLayer.select_promotion(banner.CouponID, banner.CouponNames, DataLayerPromotionLocation.LANDING_BANNER, index);
                };
            }

            bannerContainer.appendChild(element);
        }
    }

    /**
     * Sets up an IntersectionObserver to track when a single promotion is viewed with at least 80% visibility.
     * @param {HTMLElement} targetElement - The HTML element to observe.
     * @param {number} index - The index of the promotion in the list.
     * @param {number} couponId - The ID of the coupon associated with the promotion.
     * @param {string} couponName - The name of the coupon associated with the promotion.
     * @returns {IntersectionObserver} - The created IntersectionObserver instance.
     */
    private _setupPromotionObserver(targetElement: HTMLElement, index: number, couponId: number, couponName: string): IntersectionObserver {
        const observer = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting || targetElement.classList.contains("swiper-slide-active")) {
                    if (this._dataLayer)
                        this._dataLayer.view_promotion([couponId], DataLayerPromotionLocation.LANDING_BANNER, [{ id: couponId, idx: index }], couponName);

                    observer.unobserve(entry.target);
                }
            });
        }, {
            root: null,
            threshold: 0.8
        });

        observer.observe(targetElement);
        return observer;
    }

    /**
     * Initializes the carousel (swiper) for the landing page banners.
     * Retrieves rotation settings from Setting 274 (OOS.autoRotateBanners and OOS.bannerRotationInterval).
     * @param location - The carousel identifier
     * @param length - Number of banners
     */
    private _initializeBannerCarousel(location: string, length: number): void {
        if (this._swiperInstances[location])
            return;

        const settings = this._data.getBrandProperty("Settings")?.OOS;
        const autoRotate = settings?.autoRotateBanners ?? false;
        const rotationInterval = settings?.bannerRotationInterval ?? 0;

        const swiperConfig: any = {
            modules: [Pagination, Autoplay],
            loop: autoRotate && length > 1,
            slidesPerView: 1,
            pagination: {
                el: '.swiper-pagination',
                clickable: true,
                type: 'bullets',
                bulletActiveClass: 'bullet-active'
            },
        };

        if (autoRotate && rotationInterval > 0) {
            swiperConfig.autoplay = {
                delay: rotationInterval * 1000, 
                disableOnInteraction: false,
            };
        }

        const swiper = new Swiper(`.swiper-${location}-container`, swiperConfig);

        // Manually force autoplay if enabled
        if (autoRotate && rotationInterval > 0) {
            setTimeout(() => {
                swiper.autoplay?.start();
            }, 0);
        }

        this._swiperInstances[location] = swiper;
    }

    /**
     * Builds and displays the order type selection bar.
     */
    private _buildOrderTypeSelectionBar(): void {
        const orderTypeContainer = document.getElementById("div_landing_order_types");
        if (!orderTypeContainer)
            return;

        orderTypeContainer.innerHTML = "";

        const orderTypeId = this._getSelectedOrderTypeId();
        const orderSelector = new OrderTypeSelector(
            this._eventBus,
            this._data,
            this._context,
            this._localizationService,
            this._dataLayer,
            this._orderService.order,
            this._profileService.loyaltyProvider,
            this._data.getBrandProperty("OrderTypeSubTypes"),
            orderTypeId,
        );
        orderTypeContainer.appendChild(orderSelector.element);

        Util.hideElement("div_landing_order_types_placeholder");
        Util.showElement(orderTypeContainer);

        this._eventBus.emit(EventTypes.ORDER_TYPE_CHANGED, orderTypeId, this._profileService.loyaltyProvider);
    }

    private readonly _languageChanged = (): void => {
        this._updateOrderButtonText(this._orderService.order.getProperty("OrderTypeID"));
    }

    // 2DO: Maybe? Loaded Banners based on location
    // private readonly _loadStoreBannerDataAsync = async (storeKey: string | null | undefined): Promise<string> => {
    //     const response = await this._profileService.getLandingBanner(storeKey);
    //     return response;
    // }

    //async function loadLanding(): Promise<any> {
    //    aOLO.Dialog.Landing = new Landing(brandDisplayPage);
    //    //let bannerLoaded = false;
    //    //if (aOLO.User && aOLO.User.ProfileId > 0 && aOLO.User.DefaultStoreKey) {
    //    //    // Customer Default Store
    //    //    let newDesktopBanner = await Common.LoadStoreBannerData(aOLO.User.DefaultStoreKey, aOLO);
    //    //    MapBannerData(newDesktopBanner);
    //    //    bannerLoaded = true;
    //    //} else if (aOLO.Dialog.Locations && Util.isAppView() ) {
    //    //    // Geolocate the closest store
    //    //    let locations = aOLO.Dialog.Locations.GetLocations();
    //    //    if (locations && locations.length > 0) {
    //    //        let newDesktopBanner = await Common.LoadStoreBannerData(locations[0].storeKey, aOLO);
    //    //        MapBannerData(newDesktopBanner);
    //    //        bannerLoaded = true;
    //    //    }
    //    //}
    //    //if (!bannerLoaded) {
    //    //    // Brand Store or requested store in URL 
    //    //    let newDesktopBanner = await Common.LoadStoreBannerData(aOLO.brandInfo?.brandStoreKey || aOLO.storeInfo.StoreKey, aOLO);
    //    //    MapBannerData(newDesktopBanner);
    //    //}
    //    //if (aOLO.data.DesktopBanner)
    //    //    GUI_LoadLandingBanner(aOLO.data.DesktopBanner);
    //}
}